import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import type {JournalType, PrimaryFeeling, SecondaryFeeling} from '@/ts/types/dto/interventions-dto';
import type {PagedResponse} from '@/ts/types/dto/pagination-dto';

/**
 * Legacy 5-point mood scale. Still returned on entries written before the new mood
 * vocabulary and still accepted by the API, but no longer offered as input.
 */
export enum MoodLevelDTO {
  VERY_LOW = 'very_low',
  LOW = 'low',
  OKAY = 'okay',
  GOOD = 'good',
  GREAT = 'great',
}

/** The 10 moods offered for new journal entries (IPD-2872). */
export type MoodDTO =
  | 'curious'
  | 'joyful'
  | 'calm'
  | 'hopeful'
  | 'energised'
  | 'neutral'
  | 'exhausted'
  | 'tired'
  | 'sad'
  | 'angry';

/** One option in the intervention filter, named by the CMS in the user's language. */
export interface JournalInterventionDTO {
  id: UuidDTO;
  name: string;
}

/**
 * Response type for GET /api/v1/me/journals.
 *
 * A superset of the plain paged response: the same four fields plus the facet the filter bar draws
 * its options from.
 */
export interface GetJournalsResponse extends PagedResponse<JournalEntryDTO> {
  /**
   * The interventions this user has entries under, for the filter's options.
   *
   * The backend computes it ignoring the intervention and mood filters, so choosing one does not
   * collapse the dropdown to the option already chosen. It does follow the date range.
   */
  availableInterventions: JournalInterventionDTO[];
  /** Whether the same set holds any entry belonging to no intervention. */
  hasStandaloneEntries: boolean;
}

/** The literal the backend understands as "entries belonging to no intervention". */
export const STANDALONE_INTERVENTION = 'none';

/**
 * Query params for GET /api/v1/me/journals. Every filter is optional and omitting one narrows
 * nothing, so the unfiltered list is just the call with none of them.
 */
export interface GetJournalsParams {
  page?: number;
  size?: number;
  /** Filter journals from this date (inclusive). Format: yyyy-MM-dd */
  startDate?: string;
  /** Filter journals until this date (inclusive). Format: yyyy-MM-dd */
  endDate?: string;
  /** IANA timezone identifier (e.g., "Europe/Madrid"). Defaults to user's profile timezone if not sent. */
  timezone?: string;
  /** A Directus intervention id, or {@link STANDALONE_INTERVENTION} for entries under none. */
  interventionId?: string;
  /** Keep only entries carrying one of these. Legacy `moodLevel` entries match no selection. */
  moods?: MoodDTO[];
}

/**
 * Response type for POST /api/v1/me/journals
 */
export type PostJournalResponse = JournalEntryDTO;

/**
 * Request type for POST /api/v1/me/journals
 */
export interface PostJournalRequest {
  contentId: UuidDTO;
  /** Optional: an entry may carry a mood, a note, or both — but not neither. */
  answer?: {
    answer: string;
  };
  primaryFeelingIds?: string[];
  secondaryFeelingIds?: string[];
  /** Legacy scale — no longer sent by new clients. */
  moodLevel?: MoodLevelDTO;
  mood?: MoodDTO;
}

/**
 * Response type for GET /api/v1/me/journals/template
 */
export interface JournalTemplateDTO {
  journal: JournalEntryDTO;
  availableFeelings: PrimaryFeeling[];
  availableMoods: MoodDTO[];
  /** Legacy scale; empty once the backend stops serving it. */
  availableMoodLevels: MoodLevelDTO[];
}

/**
 * Journal entry with optional session and intervention metadata
 * Used for standalone journal entries that may or may not be associated with a session
 */
export interface JournalEntryDTO {
  contentId: UuidDTO;
  userComponentId?: UuidDTO;
  name: string;
  prompt: string | null;
  examples?: string | null;
  answer: string | null;
  type?: JournalType;
  isAnswered: boolean;
  createdAt?: string;
  interventionName?: string | null;
  /** Stable id for the intervention above; the name is translated. Null on a standalone entry. */
  interventionId?: UuidDTO | null;
  sessionName?: string | null;
  /** Stable id for the session above; the name is translated. Null on a standalone entry. */
  sessionId?: UuidDTO | null;
  /** The session's place in its own intervention, from 1. Null once a session is retired. */
  sessionNumber?: number | null;
  primaryFeelings?: PrimaryFeeling[] | null;
  secondaryFeelings?: SecondaryFeeling[] | null;
  /** Legacy scale — present only on entries written before the new moods. */
  moodLevel?: MoodLevelDTO | null;
  mood?: MoodDTO | null;
}

/**
 * The mood a user recorded on one day. Nothing is averaged: a mood is a name, not a point on a
 * scale, so the day carries the mood they picked — the latest one, if they logged several.
 */
export interface DayMoodDTO {
  /** yyyy-MM-dd */
  date: string;
  mood: MoodDTO;
}

/**
 * Response from the day-moods endpoint, feeding the mood map.
 *
 * Only days carrying a mood appear. A day the user did not log is absent rather than null — the
 * chart draws those as untracked. Days are not promised in any particular order, so a caller that
 * needs the earliest must take the minimum rather than the first element.
 * Entries written with the legacy {@link MoodLevelDTO} never appear here.
 */
export interface DayMoodsResponse {
  days: DayMoodDTO[];
}

/**
 * Query parameters for the mood map's data. Both bounds are optional: omit them for the user's
 * whole history, which is what "all time" sends, having no start date of its own to give.
 */
export interface GetDayMoodsParams {
  /** yyyy-MM-dd, inclusive. */
  startDate?: string;
  /** yyyy-MM-dd, inclusive. */
  endDate?: string;
  timezone?: string;
}
